import React, { useRef, useEffect, useCallback } from 'react';
import { Box, Typography, CircularProgress, Divider, Chip, Tooltip } from '@mui/material';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useAppSelector, useAppDispatch } from '../hooks/reduxHooks';
import {
  startEditingCorrected,
  updateEditedCorrectedText,
  setCursorPosition,
  clearDictationDelta,
  startManualTyping,
  updateManualTypedText,
} from '../store/transcriptionSlice';

interface TranscriptPanelProps {
  isTranscribing: boolean;
  isReadOnly?: boolean;
  showDictationCapture?: boolean;
  hasUnsavedChanges?: boolean;
}

/**
 * Dual-stream transcript panel.
 *
 * Renders two independent transcript views:
 *   - **Raw transcript** (top) — ASR output, italic, light gray, smaller emphasis
 *   - **Corrected transcript** (bottom, primary) — LLM-corrected text, normal weight, dark color
 *
 * During transcription the raw stream updates immediately while corrected
 * text appears asynchronously as the LLM returns results.
 */
const TranscriptPanel: React.FC<TranscriptPanelProps> = ({ 
  isTranscribing, 
  isReadOnly = false, 
  showDictationCapture = true,
  hasUnsavedChanges = false,
}) => {
  const {
    rawText, correctedText,
    isIncrementalCorrecting, isRefiningInFlight,
    isEditingCorrected, editedCorrectedText, isUserEdited,
    latestDictationDelta,
    isManualTypingMode, manualTypedText,
    lastCorrectionFailed,
  } = useAppSelector((state) => state.transcription);
  const dispatch = useAppDispatch();
  const correctedRef = useRef<HTMLDivElement>(null);
  const rawRef = useRef<HTMLDivElement>(null);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-scroll both panels when text changes
  useEffect(() => {
    if (correctedRef.current) {
      correctedRef.current.scrollTop = correctedRef.current.scrollHeight;
    }
  }, [correctedText]);

  useEffect(() => {
    if (rawRef.current) {
      rawRef.current.scrollTop = rawRef.current.scrollHeight;
    }
  }, [rawText]);

  useEffect(() => {
    if (isEditingCorrected && textAreaRef.current) {
      textAreaRef.current.focus();
      textAreaRef.current.select();
    }
  }, [isEditingCorrected]);

  // Track cursor position to prevent race condition in simultaneous typing + dictation.
  // Debounced to avoid dispatching on every keystroke — only the final position after
  // 150ms of inactivity is sent to Redux (which may trigger pendingDictationQueue flush).
  const cursorDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const debouncedSetCursor = useCallback((pos: number) => {
    if (cursorDebounceRef.current) clearTimeout(cursorDebounceRef.current);
    cursorDebounceRef.current = setTimeout(() => {
      dispatch(setCursorPosition(pos));
    }, 150);
  }, [dispatch]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (cursorDebounceRef.current) clearTimeout(cursorDebounceRef.current);
    };
  }, []);

  const handleCursorChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    debouncedSetCursor(e.target.selectionStart || 0);
  };

  const handleMouseClick = (e: React.MouseEvent<HTMLTextAreaElement>) => {
    debouncedSetCursor(e.currentTarget.selectionStart || 0);
  };

  const handleKeyUp = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    debouncedSetCursor(e.currentTarget.selectionStart || 0);
  };

  // When dictation appends to the edited textarea during simultaneous typing,
  // scroll the textarea to the bottom so the new dictated text is visible.
  useEffect(() => {
    if (isEditingCorrected && latestDictationDelta && textAreaRef.current) {
      const el = textAreaRef.current;
      // Defensive programming: ensure element exists and has scrollHeight
      if (el && typeof el.scrollHeight === 'number') {
        el.scrollTop = el.scrollHeight;
      }
    }
  }, [latestDictationDelta, isEditingCorrected]);

  // Auto-clear the dictation delta after 3 seconds so the "Dictating..." chip doesn't stay visible indefinitely
  useEffect(() => {
    if (!latestDictationDelta) return;
    const timeoutId = setTimeout(() => {
      dispatch(clearDictationDelta());
    }, 3000);
    return () => clearTimeout(timeoutId);
  }, [latestDictationDelta, dispatch]);

  const handleEditTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    // Enter edit mode BEFORE updating text — startEditingCorrected resets
    // editedCorrectedText to correctedText, so it must fire first or it will
    // overwrite the character the user just typed.
    if (!isEditingCorrected && e.target.value.length > 0) {
      dispatch(startEditingCorrected());
    }
    dispatch(updateEditedCorrectedText(e.target.value));
  };

  // Manual typing handlers (click DICTATION area to type without recording)
  const handleDictationAreaClick = () => {
    // Only enter manual typing mode if no text exists and not already in another mode
    if (!correctedText && !rawText && !isManualTypingMode && !isEditingCorrected && !isTranscribing) {
      dispatch(startManualTyping());
    }
  };

  // Click raw text to start editing it as corrected text
  const handleRawTextEditClick = () => {
    if (isReadOnly) return;
    if (rawText && !correctedText && !isManualTypingMode && !isEditingCorrected && !isTranscribing) {
      dispatch(startEditingCorrected());
      dispatch(updateEditedCorrectedText(rawText));
    }
  };

  const handleManualTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    dispatch(updateManualTypedText(e.target.value));
  };

  const hasCorrected = correctedText.length > 0;
  const showRefining = isRefiningInFlight && !isIncrementalCorrecting;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
      }}
    >
      {/* ── Raw ASR transcript (top, secondary) ───────────────────── */}
      {/* Gap 1: Only show Dictation Capture when toggled on */}
      {showDictationCapture && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Typography
              variant="caption"
              sx={{ color: 'text.disabled', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}
            >
              Dictation Capture
            </Typography>
            {isTranscribing && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  ml: 1,
                  px: 1,
                  py: 0.25,
                  borderRadius: 1,
                  bgcolor: 'error.main',
                  color: 'error.contrastText',
                  animation: 'pulse 1.5s infinite',
                  '@keyframes pulse': {
                    '0%': { opacity: 1 },
                    '50%': { opacity: 0.7 },
                    '100%': { opacity: 1 },
                  },
                }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: 'background.paper',
                    mr: 0.5,
                  }}
                />
                <Typography variant="caption" sx={{ color: 'inherit', fontWeight: 600, fontSize: '0.65rem' }}>
                  Recording
                </Typography>
              </Box>
            )}
          </Box>
          <Box
            ref={rawRef}
            sx={{
              flex: '0 0 auto',
              maxHeight: 180,
              minHeight: 96,
              overflowY: 'auto',
              bgcolor: (theme) => theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[50],
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              px: 2,
              py: 1.5,
              mb: 1,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                whiteSpace: 'pre-wrap',
                fontStyle: 'italic',
                color: 'text.secondary',
                fontSize: '0.85rem',
                lineHeight: 1.6,
              }}
            >
              {rawText ||
                (isTranscribing
                  ? 'Listening...'
                  : 'No transcription yet.')}
            </Typography>
          </Box>
          <Divider sx={{ mb: 1 }} />
        </>
      )}

      {/* ── Corrected / Primary transcript (bottom, primary) ──────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}
          >
            {hasCorrected ? 'Reviewed Dictation' : 'Dictation'}
          </Typography>
          {isUserEdited && !isEditingCorrected && (
            <Chip label="Edited" size="small" color="info" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />
          )}
          {isIncrementalCorrecting && isTranscribing && (
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 0.5,
              bgcolor: 'success.main', color: 'success.contrastText', borderRadius: 1,
              px: 0.75, py: 0.15,
            }}>
              <Box sx={{
                width: 6, height: 6, borderRadius: '50%', bgcolor: 'background.paper',
                animation: 'pulse 1.2s infinite',
                '@keyframes pulse': { '0%': { opacity: 1 }, '50%': { opacity: 0.3 }, '100%': { opacity: 1 } },
              }} />
              <Typography variant="caption" sx={{ fontSize: '0.6rem', fontWeight: 600 }}>
                LIVE
              </Typography>
            </Box>
          )}
          {showRefining && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CircularProgress size={10} />
              <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.65rem' }}>
                Refining text...
              </Typography>
            </Box>
          )}
          {lastCorrectionFailed && (
            <Tooltip title="Live correction temporarily unavailable. Raw transcript shown." arrow>
              <Chip
                icon={<WarningAmberIcon fontSize="small" />}
                label="Correction unavailable"
                size="small"
                color="warning"
                variant="outlined"
                sx={{ height: 18, fontSize: '0.6rem' }}
              />
            </Tooltip>
          )}
        </Box>
        {/* Gap 4: Auto-save indicator - shows saved/unsaved state */}
        {!isReadOnly && hasCorrected && (
          <Tooltip title={hasUnsavedChanges ? "Unsaved changes" : "All changes saved"} arrow>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {hasUnsavedChanges ? (
                <CloudOffIcon fontSize="small" color="warning" />
              ) : (
                <CloudDoneIcon fontSize="small" color="success" />
              )}
              <Typography variant="caption" sx={{ color: hasUnsavedChanges ? 'warning.main' : 'success.main', fontSize: '0.65rem' }}>
                {hasUnsavedChanges ? 'Unsaved' : 'Saved'}
              </Typography>
            </Box>
          </Tooltip>
        )}
        {/* Gap 3: Edit/Save/Cancel buttons removed - now using click-to-edit with auto-save */}
      </Box>

      <Box
        ref={correctedRef}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          px: 2,
          py: 1.5,
        }}
      >
        {isManualTypingMode ? (
          <textarea
            value={manualTypedText}
            onChange={handleManualTextChange}
            autoFocus
            placeholder="Type your dictation here..."
            style={{
              width: '100%',
              height: '100%',
              padding: '8px',
              border: 'none',
              outline: 'none',
              fontFamily: 'inherit',
              fontSize: '0.95rem',
              lineHeight: '1.7',
              resize: 'none',
              color: 'text.primary',
            }}
          />
        ) : isEditingCorrected ? (
          <textarea
            ref={textAreaRef}
            value={editedCorrectedText}
            onChange={handleEditTextChange}
            onSelect={handleCursorChange}
            onClick={handleMouseClick}
            onKeyUp={handleKeyUp}
            style={{
              width: '100%',
              height: '100%',
              padding: '8px',
              border: 'none',
              outline: 'none',
              fontFamily: 'inherit',
              fontSize: '0.95rem',
              lineHeight: '1.7',
              resize: 'none',
              color: 'text.primary',
            }}
          />
        ) : hasCorrected ? (
          <Box
            onClick={!isReadOnly ? () => dispatch(startEditingCorrected()) : undefined}
            sx={{
              cursor: !isReadOnly ? 'text' : 'default',
              height: '100%',
              '&:hover': !isReadOnly ? {
                bgcolor: 'action.hover',
              } : undefined,
            }}
          >
            <Typography
              variant="body1"
              sx={{
                whiteSpace: 'pre-wrap',
                color: 'text.primary',
                fontWeight: 400,
                fontSize: '0.95rem',
                lineHeight: 1.7,
              }}
            >
              {correctedText}
            </Typography>
          </Box>
        ) : (
          <Box
            onClick={rawText ? handleRawTextEditClick : handleDictationAreaClick}
            sx={{
              cursor: (!correctedText && !rawText && !isTranscribing) || (rawText && !correctedText && !isReadOnly) ? 'text' : 'default',
              height: '100%',
              '&:hover': rawText && !correctedText && !isReadOnly ? {
                bgcolor: 'action.hover',
              } : undefined,
            }}
          >
            <Typography
              variant="body1"
              sx={{
                whiteSpace: 'pre-wrap',
                color: showRefining ? 'text.disabled' : 'text.primary',
                fontWeight: 400,
                fontSize: '0.95rem',
                lineHeight: 1.7,
              }}
            >
              {showRefining
                ? 'Refining dictation text...'
                : (isTranscribing
                  ? 'Listening...'
                  : (rawText || 'No dictation yet. Click here to type manually, or "Start Dictation" to record.'))}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default TranscriptPanel;
